// modules
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { sortBy } from "lodash";

// constants
import { GROUP_STATE, RoleManagement as Constants, Routes } from "constants/global";
import SearchFilterIcon from "components/atoms/SearchFilterIcon/SearchFilterIcon";

// redux
import {
	getGroupsList,
	groupsListSelector,
	updateUserSearchKeyPressed,
	toggleFilter,
	showHideFilter,
	updateCurrentGroup,
	getUserPermissionListSelector,
	selectedCurrentGroup,
	groupsListLoading
} from "redux/userList/userSlice";

// hooks
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// component
import Button from "components/atoms/Button/Button";
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// styles
import styles from "containers/RoleManagement/roleManagementComponent.module.css";

// types
import { GroupDetails } from "containers/RoleManagement/RoleManagementPage";

// services
import { getExportUserList as APIGetExportUserList } from "services/api/roleManagement";

// utils
import { downloadFile } from "utils/downloadFile";

export type SelectedItem = {
	name: string;
	value: string;
	checked: boolean;
};

export type FilterGroupItem = {
	title: string;
	values: Array<{
		id: string;
		value: string;
	}>;
};
export interface FilterItem {
	[key: string]: boolean;
}

export type UserDetail = {
	user_id: number;
	username: string;
	email: string;
	first_name: string;
	last_name: string;
	mfa_enabled: string;
	tenant_code: string;
	tenant_name: string;
	pwd_updated_on: string;
	active: string;
	retro_appr_limit: number;
	reimb_appr_limit: number;
	created_by: string;
	created_on: string;
	modified_by: string;
	modified_on: string;
	groups: Array<string>;
};

export type RoleManagementPropTypes = {
	getGroupHeader: (_voucherTypeHandler?: () => void, _voucherDownloadHandler?: () => void, groupData?: any) => void;
	generateCols: () => void;
	handleSearchGroup: (input: any) => void;
	handleKeyDown: () => void;
	handleUserSearch: (input: any) => void;
	updateEnterKeyEvent: () => void;
	handleExpandRows: (val: string) => void;
	loading: boolean;
	groupListLoading: boolean;
	updatedUserGroupList?: Array<GroupDetails>;
	searchGroup: string;
	searchUser: string;
	currentExpandedGroup: string | undefined;
	createUserAccess: boolean;
	createGroupAccess: boolean;
};

export function useRoleManagement(): RoleManagementPropTypes {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const { data } = useAppSelector(groupsListSelector);
	const groupListLoading = useAppSelector(groupsListLoading) === 1;
	const showFilter = useAppSelector(showHideFilter);
	const userPermissionList = useAppSelector(getUserPermissionListSelector);

	const [groupSearchEntered, setGroupSearchEntered] = useState(false);
	const [searchGroup, setSearchGroup] = useState("");
	const [searchUser, setSearchUser] = useState("");
	const [currentExpandedGroup, setCurrentExpandedGroup] = useState<string>();
	const selectedGroup = useAppSelector(selectedCurrentGroup);
	const [loading, setLoading] = useState<boolean>(false);

	const filterData: FilterItem = {};

	useEffect(() => {
		if (!data) {
			dispatch(getGroupsList({}));
		}
		dispatch(toggleFilter({}));
		dispatch(updateCurrentGroup(Constants.ALL_USERS_GROUP.group_code));
	}, []);

	useEffect(() => {
		if (selectedGroup !== Constants.ALL_USERS_GROUP.group_code) {
			setSearchUser("");
		}
	}, [selectedGroup]);

	const createUserAccess = useMemo(() => {
		return !!userPermissionList.find(({ perm_code }) => perm_code === Constants.CREATE_EDIT_ADMIN_ACCOUNT);
	}, [userPermissionList]);

	const createGroupAccess = useMemo(() => {
		return !!userPermissionList.find(({ perm_code }) => perm_code === Constants.CREATE_EDIT_GROUP);
	}, [userPermissionList]);

	const updatedUserGroupList = useMemo(() => {
		if (data && data.length > 0) {
			const groupList = data?.filter(
				(item: GroupDetails) => item.group_code.toUpperCase().indexOf(searchGroup.toUpperCase()) > -1 || item.group_desc.toUpperCase().indexOf(searchGroup.toUpperCase()) > -1
			);
			setGroupSearchEntered(false);
			if (!searchGroup) {
				return [Constants.ALL_USERS_GROUP, ...groupList];
			} else {
				return [...groupList];
			}
		}
	}, [data, groupSearchEntered, setGroupSearchEntered]);

	const handleSearchGroup = (event: any) => {
		event.preventDefault();
		setSearchGroup(event.target.value);
	};

	const handleKeyDown = () => {
		setGroupSearchEntered(true);
	};

	const handleUserSearch = (event: any) => {
		event.preventDefault();
		setSearchUser(event.target.value);
		setCurrentExpandedGroup(Constants.ALL_USERS_GROUP.group_code);
		setSearchGroup("");
		handleKeyDown();
	};

	const updateEnterKeyEvent = () => {
		dispatch(updateUserSearchKeyPressed(true));
	};

	const handleToggleFilter = (filterValues: FilterItem, groupCode: string) => {
		filterValues[groupCode] = !showFilter[groupCode];
		dispatch(updateCurrentGroup(groupCode));
		dispatch(toggleFilter(filterValues));
	};

	const addViewEditButtonHandler = (group_code: string) => {
		navigate(Routes.EDIT_GROUP, { state: { group_code } });
	};

	const onFailure = useCallback(() => {
		setLoading(false);
		CountDownModal({
			timer: 3,
			title: Constants.NO_DATA_FOUND,
			errorIcon: true
		});
	}, [CountDownModal]);

	const handleExportUserList = useCallback(
		async (code: string) => {
			try {
				setLoading(true);
				const { data: userListData } = code !== Constants.ALL_USERS_GROUP.group_code ? await APIGetExportUserList({ group_code: code }) : await APIGetExportUserList({});
				const { data: userListItemData, errors } = userListData;

				if (userListItemData?.file_url && !errors?.length) {
					try {
						await downloadFile(userListItemData?.file_url);

						CountDownModal({
							timer: 3,
							title: Constants.REPORT_GENERATED
						});
						setLoading(false);
					} catch (error) {
						onFailure();
					}
				} else {
					onFailure();
				}
			} catch (error) {
				onFailure();
			}
		},
		[loading, onFailure, downloadFile, CountDownModal, APIGetExportUserList]
	);

	const getGroupHeader = (_voucherTypeHandler?: () => void, _voucherDownloadHandler?: () => void, groupData?: Array<any>) => {
		return (
			data &&
			sortBy(groupData, ["group_code"]).map((item) => {
				const key = item.group_code;

				filterData[item.group_code] = showFilter && showFilter[item.group_code] ? !showFilter[item.group_code] : false;

				return {
					cell1: <p className={styles.groupTitle}>{`${item.group_code} ${item?.active === GROUP_STATE.inActive ? Constants.DEACTIVATED : ""}`}</p>,
					cell2:
						item.group_code !== Constants.ALL_USERS_GROUP.group_code ? (
							<Button label={Constants.VIEW_EDIT_GROUP_LABEL} onClick={() => addViewEditButtonHandler(item?.group_code)} background="yellow" />
						) : (
							<p></p>
						),
					cell3: <Button label={Constants.EXPORT_USER_LIST_LABEL} onClick={() => handleExportUserList(item?.group_code)} background="white" />,

					cell4: (
						<SearchFilterIcon
							label={Constants.FILTER_LABEL}
							defaultSelectedFilterValues={[Constants.DEFAULT_STATUS[0].value, Constants.DEFAULT_STATUS[1].value]}
							defaultSelectedFilter={Constants.DEFAULT_STATUS}
							groupCode={item.group_code}
							onClick={() => handleToggleFilter(filterData, item.group_code)}
						/>
					),
					key: key,
					id: key
				};
			})
		);
	};
	const generateCols = () => {
		return [
			{
				dataIndex: "cell1",
				title: <p></p>,
				width: "90%"
			},
			{
				dataIndex: "cell2",
				title: <p></p>
			},
			{
				dataIndex: "cell3",
				title: <p></p>
			},
			{
				dataIndex: "cell4",
				title: <p></p>
			}
		];
	};

	const handleExpandRows = useCallback(
		(_value: string) => {
			setCurrentExpandedGroup(_value);
		},
		[currentExpandedGroup]
	);

	return {
		updatedUserGroupList,
		searchGroup,
		searchUser,
		currentExpandedGroup,
		loading,
		createUserAccess,
		createGroupAccess,
		groupListLoading,
		getGroupHeader,
		generateCols,
		handleSearchGroup,
		handleKeyDown,
		handleUserSearch,
		updateEnterKeyEvent,
		handleExpandRows
	};
}
